/**
 * Canvas rendering of speech bubbles, for the timelapse export.
 *
 * On screen a bubble is an HTML element (`SpeechBubbles.tsx` + the
 * `.speech-bubble` rules in global.scss). The recorder, however, captures the
 * PIXI canvas only — HTML overlays are not part of a canvas stream — so a
 * replay that contained speech used to export without a single bubble in it.
 * This module draws the same bubbles onto the export surface.
 *
 * It deliberately mirrors the DOM version rather than sharing code with it,
 * because the two live in different worlds: CSS does wrapping, rounded
 * corners, the tail and the lifespan animation for us; here every one of
 * those has to be drawn by hand. What MUST stay in sync is the geometry —
 * anchor point, the lift above the pixel, max width, padding, font size — so
 * the exported clip frames a bubble exactly where the viewer saw it. Those
 * numbers are named below and match global.scss one to one.
 */

import { SpeechBubbleStyle } from './speechStyles';

export interface BubbleSpeech {
  pointId: number;
  text: string;
  style: SpeechBubbleStyle;
  /** performance-independent birth stamp (Date.now()), for the fade. */
  ts: number;
}

export interface BubblePoint { id: number; x: number; y: number; }
export interface BubbleViewport { x: number; y: number; zoom: number; }

export interface DrawBubblesOptions {
  speeches: BubbleSpeech[];
  points: BubblePoint[];
  viewport: BubbleViewport;
  /** CSS size of the on-screen canvas the coordinates were computed against. */
  windowSize: { width: number; height: number };
  /** Multiplier from CSS px to output px (output width / window width). */
  scale: number;
  /** Now, in the same clock as `ts`. Defaults to Date.now(). */
  now?: number;
}

// ── numbers shared with .speech-bubble in global.scss ──────────────────
const MAX_W_DESKTOP = 188;
const MAX_W_MOBILE  = 148;
const MOBILE_MAX_PX = 480;
const FONT_DESKTOP  = 12;
const FONT_MOBILE   = 10;
const PAD_X_DESKTOP = 12;
const PAD_Y_DESKTOP = 8;
const PAD_X_MOBILE  = 10;
const PAD_Y_MOBILE  = 7;
const LINE_HEIGHT   = 1.3;
const TAIL          = 12;     // gap between the pixel and the bubble's bottom
const DOT_R         = 2;      // the ::before dot, 4px across
const LIFE_MS       = 5000;   // .speech-bubble animation duration
const GAP           = 4;      // anti-overlap gap, as in SpeechBubbles.tsx
const STACK_H       = 50;     // BUBBLE_H there — approximate, for stacking only

interface Skin {
  bg: string;
  border: string;
  borderWidth: number;
  radius: number;
  text: string;
  tail: string;
  font: (size: number) => string;
  upper?: boolean;
  letterSpacing?: number;
  ring?: string;               // cloud: soft outer ring
  glow?: string;               // neon
  hardShadow?: boolean;        // pixel: offset block shadow
}

const SKINS: Record<SpeechBubbleStyle, Skin> = {
  default: {
    bg: 'rgba(255,255,255,0.97)', border: 'rgba(17,17,17,0.72)', borderWidth: 1,
    radius: 10, text: '#111', tail: 'rgba(17,17,17,0.72)',
    font: (s) => `600 ${s}px 'Trebuchet MS', Arial, sans-serif`,
  },
  cloud: {
    bg: 'rgba(255,255,255,0.97)', border: 'rgba(17,17,17,0.72)', borderWidth: 1,
    radius: 18, text: '#111', tail: 'rgba(120,120,120,0.85)',
    font: (s) => `600 ${s}px 'Trebuchet MS', Arial, sans-serif`,
    ring: 'rgba(255,255,255,0.30)',
  },
  neon: {
    bg: 'rgba(7,15,20,0.94)', border: 'rgba(90,255,214,0.85)', borderWidth: 1,
    radius: 10, text: '#c9fff2', tail: 'rgba(90,255,214,0.85)',
    font: (s) => `600 ${s}px 'Trebuchet MS', Arial, sans-serif`,
    glow: 'rgba(90,255,214,0.32)',
  },
  pixel: {
    bg: 'rgba(255,255,255,0.97)', border: 'rgba(17,17,17,0.72)', borderWidth: 2,
    radius: 0, text: '#111', tail: 'rgba(17,17,17,0.72)',
    font: (s) => `600 ${s}px 'Courier New', Courier, monospace`,
    upper: true, letterSpacing: 0.6, hardShadow: true,
  },
};

/** Opacity over a bubble's life — the speech-bubble-life keyframes. */
function lifeAlpha(age: number): number {
  const t = age / LIFE_MS;
  if (t < 0) return 0;
  if (t >= 1) return 0;
  if (t < 0.10) return t / 0.10;
  if (t < 0.78) return 1;
  return 1 - (t - 0.78) / 0.22;
}

/** Extra lift over a bubble's life, in CSS px (same keyframes). */
function lifeLift(age: number): number {
  const t = Math.max(0, Math.min(1, age / LIFE_MS));
  if (t < 0.10) return 4 + (12 - 4) * (t / 0.10);
  if (t < 0.78) return 12;
  return 12 + (18 - 12) * ((t - 0.78) / 0.22);
}

function wrap(ctx: CanvasRenderingContext2D, text: string, maxW: number): string[] {
  // pre-wrap in CSS: author's own newlines are kept, the rest wraps by word,
  // and a word longer than the bubble is broken (word-break: break-word).
  const out: string[] = [];
  for (const paragraph of text.split('\n')) {
    let line = '';
    for (const word of paragraph.split(/\s+/)) {
      if (!word) continue;
      const trial = line ? `${line} ${word}` : word;
      if (ctx.measureText(trial).width <= maxW || !line) {
        if (ctx.measureText(trial).width <= maxW) { line = trial; continue; }
        // single word wider than the bubble — break it by characters
        let chunk = '';
        for (const ch of trial) {
          if (ctx.measureText(chunk + ch).width > maxW && chunk) {
            out.push(chunk);
            chunk = ch;
          } else {
            chunk += ch;
          }
        }
        line = chunk;
      } else {
        out.push(line);
        line = word;
      }
    }
    out.push(line);
  }
  return out.length ? out : [''];
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number,
                   w: number, h: number, r: number) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

/**
 * Draw every live bubble onto `ctx`, which is the export surface.
 *
 * Coordinates come in as CSS px (the same maths SpeechBubbles.tsx does) and
 * are multiplied by `scale` on the way out, so the export keeps its own
 * resolution independent of the screen.
 */
export function drawSpeechBubbles(ctx: CanvasRenderingContext2D,
                                  o: DrawBubblesOptions): void {
  if (!o.speeches.length) return;
  const now = o.now ?? Date.now();
  const { width: W, height: H } = o.windowSize;
  const { x: vpX, y: vpY, zoom } = o.viewport;
  const mobile  = W <= MOBILE_MAX_PX;
  const maxW    = mobile ? MAX_W_MOBILE : MAX_W_DESKTOP;
  const fontPx  = mobile ? FONT_MOBILE : FONT_DESKTOP;
  const padX    = mobile ? PAD_X_MOBILE : PAD_X_DESKTOP;
  const padY    = mobile ? PAD_Y_MOBILE : PAD_Y_DESKTOP;
  const s       = o.scale;

  type Item = {
    text: string; skin: Skin; alpha: number; lift: number;
    cx: number; bx: number; anchorY: number; adjAnchorY: number;
  };

  const items: Item[] = [];
  for (const sp of o.speeches) {
    const pt = o.points.find(p => p.id === sp.pointId);
    if (!pt) continue;
    const age = now - sp.ts;
    const alpha = lifeAlpha(age);
    if (alpha <= 0) continue;
    const cx = (pt.x - vpX) * zoom + W / 2 + zoom / 2;
    const anchorY = (vpY - pt.y) * zoom + H / 2;
    items.push({
      text: sp.text, skin: SKINS[sp.style] ?? SKINS.default,
      alpha, lift: lifeLift(age),
      cx, bx: cx - maxW / 2, anchorY, adjAnchorY: anchorY,
    });
  }
  if (!items.length) return;

  // Same anti-overlap rule as on screen: a bubble that would collide is
  // pushed up so they stack instead of covering each other.
  items.sort((a, b) => a.anchorY - b.anchorY);
  for (let i = 1; i < items.length; i++) {
    for (let j = 0; j < i; j++) {
      const a = items[j], b = items[i];
      const hOverlap = b.bx < a.bx + maxW + GAP && b.bx + maxW + GAP > a.bx;
      if (hOverlap && b.adjAnchorY < a.adjAnchorY + STACK_H + GAP) {
        b.adjAnchorY = a.adjAnchorY - STACK_H - GAP;
      }
    }
  }

  for (const it of items) {
    const skin = it.skin;
    ctx.save();
    ctx.globalAlpha = it.alpha;
    ctx.font = skin.font(fontPx * s);
    ctx.textBaseline = 'top';
    ctx.textAlign = 'center';

    const body  = skin.upper ? it.text.toLocaleUpperCase() : it.text;
    const inner = (maxW - padX * 2) * s;
    const lines = wrap(ctx, body, inner);
    const lineH = fontPx * LINE_HEIGHT * s;
    const textW = Math.max(...lines.map(l => ctx.measureText(l).width));
    const boxW  = Math.min(maxW * s, textW + padX * 2 * s);
    const boxH  = lines.length * lineH + padY * 2 * s;

    const cx     = it.cx * s;
    const anchor = it.adjAnchorY * s;
    const boxX   = cx - boxW / 2;
    const boxY   = anchor - it.lift * s - boxH;

    if (skin.ring) {                       // cloud: soft halo around the box
      ctx.fillStyle = skin.ring;
      roundRect(ctx, boxX - 3 * s, boxY - 3 * s, boxW + 6 * s, boxH + 6 * s,
                (skin.radius + 3) * s);
      ctx.fill();
    }
    if (skin.hardShadow) {                 // pixel: offset block shadow
      ctx.fillStyle = 'rgba(17,17,17,0.35)';
      ctx.fillRect(boxX + 4 * s, boxY + 4 * s, boxW, boxH);
    }
    if (skin.glow) {                       // neon
      ctx.shadowColor = skin.glow;
      ctx.shadowBlur  = 16 * s;
    }
    ctx.fillStyle = skin.bg;
    roundRect(ctx, boxX, boxY, boxW, boxH, skin.radius * s);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.lineWidth = skin.borderWidth * s;
    ctx.strokeStyle = skin.border;
    roundRect(ctx, boxX, boxY, boxW, boxH, skin.radius * s);
    ctx.stroke();

    // Tail: a hairline down to the pixel, with a dot at the tip.
    ctx.fillStyle = skin.tail;
    ctx.fillRect(cx - 0.5 * s, boxY + boxH, Math.max(1, s), anchor - (boxY + boxH));
    ctx.beginPath();
    ctx.arc(cx, anchor, DOT_R * s, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = skin.text;
    lines.forEach((line, i) => {
      ctx.fillText(line, cx, boxY + padY * s + i * lineH);
    });
    ctx.restore();
  }
}
